/**
 * Role + flag access helpers. Catalog owns the maps; this file only
 * answers "can this user see X?"
 */

import { isAdmin, isTeamLeader, normalizeRole } from '../lib/roleUtils';
import {
  ADMIN_NAV_ITEMS,
  ALL_NAV_ITEM_IDS,
  COMPANY_TAB_PERMISSIONS,
  EMPLOYEE_NAV_ITEMS,
  FIELD_PERMISSIONS,
  MASKED_PLACEHOLDER,
  NAV_ITEM_PERMISSIONS,
  REPORT_ITEM_PERMISSIONS,
  RESOURCE_ACTIONS,
  ROUTE_PERMISSIONS,
  TEAM_LEADER_SELF_NAV_ITEMS,
} from './catalog';
import { isGranted, normalizePermissions } from './normalizePermissions';

export {
  ADMIN_NAV_ITEMS,
  ALL_NAV_ITEM_IDS,
  COMPANY_TAB_PERMISSIONS,
  EMPLOYEE_NAV_ITEMS,
  NAV_ITEM_PERMISSIONS,
  REPORT_ITEM_PERMISSIONS,
  ROUTE_PERMISSIONS,
  TEAM_LEADER_BASE_NAV_ITEMS,
  TEAM_LEADER_SELF_NAV_ITEMS,
} from './catalog';

function permissionMap(userOrPermissions) {
  if (!userOrPermissions) return null;
  if (userOrPermissions.permissions && typeof userOrPermissions.permissions === 'object') {
    return normalizePermissions(userOrPermissions.permissions);
  }
  if (userOrPermissions.role || userOrPermissions.userId || userOrPermissions.id) {
    return userOrPermissions.permissions
      ? normalizePermissions(userOrPermissions.permissions)
      : null;
  }
  return normalizePermissions(userOrPermissions);
}

export function hasCapability(userOrPermissions, ...keys) {
  const perms = permissionMap(userOrPermissions);
  if (!perms || keys.length === 0) return false;
  return keys.some((key) => isGranted(perms[key]));
}

export function getRoutePermissionKeys(path) {
  if (!path) return undefined;
  if (Object.prototype.hasOwnProperty.call(ROUTE_PERMISSIONS, path)) {
    return ROUTE_PERMISSIONS[path];
  }
  const prefixes = Object.keys(ROUTE_PERMISSIONS).sort((a, b) => b.length - a.length);
  for (const prefix of prefixes) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      return ROUTE_PERMISSIONS[prefix];
    }
  }
  return undefined;
}

export function canAccessNavItem(user, itemId) {
  if (!user || !itemId) return false;
  const role = normalizeRole(user.role);

  if (itemId === 'settings') return isAdmin(role);
  if (itemId === 'overview') return false;
  if (itemId === 'notifications') return true;

  if (isAdmin(role)) {
    if (itemId === 'salary') return false;
    return ADMIN_NAV_ITEMS.includes(itemId);
  }

  if (isTeamLeader(role)) {
    if (TEAM_LEADER_SELF_NAV_ITEMS.includes(itemId)) return true;
    const keys = NAV_ITEM_PERMISSIONS[itemId];
    if (!keys) return false;
    return hasCapability(user, ...keys);
  }

  if (!EMPLOYEE_NAV_ITEMS.includes(itemId)) return false;
  if (itemId === 'permissions') {
    return hasCapability(user, ...(NAV_ITEM_PERMISSIONS.permissions || []));
  }
  return true;
}

export function canAccessCompanyTab(user, tabId) {
  if (!user || !tabId) return false;
  if (isAdmin(user.role)) return true;
  if (!isTeamLeader(user.role)) return false;
  if (!Object.prototype.hasOwnProperty.call(COMPANY_TAB_PERMISSIONS, tabId)) {
    return false;
  }
  const keys = COMPANY_TAB_PERMISSIONS[tabId];
  if (keys == null) return false;
  return hasCapability(user, ...keys);
}

export function canAccessManagerDashboard(user) {
  if (!user || typeof user !== 'object') return false;
  if (isAdmin(user.role) || user.role === 'manager') return true;
  return isTeamLeader(user.role) && hasCapability(user, 'dashboard');
}

export function canAccessReport(user, reportId) {
  if (isAdmin(user?.role)) return true;
  if (!isTeamLeader(user?.role)) return false;
  const id = Number(reportId);
  const keys = REPORT_ITEM_PERMISSIONS[id];
  if (!Array.isArray(keys) || keys.length === 0) return false;
  if (!hasCapability(user, ...keys)) return false;
  if ([6, 7, 8, 9].includes(id) && !canAccessNavItem(user, 'employees')) {
    return false;
  }
  if (id === 11 && !canAccessNavItem(user, 'company')) {
    return false;
  }
  if (id === 12 && !hasCapability(user, 'reports_attendance')) {
    return false;
  }
  return true;
}

export function canAccessAdminRoute(user, permissionKeys) {
  if (isAdmin(user?.role)) return true;
  if (!isTeamLeader(user?.role)) return false;
  if (permissionKeys == null) return false;
  if (!Array.isArray(permissionKeys) || permissionKeys.length === 0) return false;
  return hasCapability(user, ...permissionKeys);
}

export function toAccessUser(userOrRole) {
  if (userOrRole && typeof userOrRole === 'object') return userOrRole;
  return { role: userOrRole };
}

export function getSessionUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
}

/**
 * Action-level check: can this user view/create/edit/delete/approve a resource?
 * Admins always can. Team leaders need a matching catalog flag.
 */
export function canPerform(user, resource, action = 'view') {
  const accessUser = toAccessUser(user);
  if (!accessUser || !resource || !action) return false;
  if (isAdmin(accessUser.role)) return true;
  if (!isTeamLeader(accessUser.role)) return false;
  const spec = RESOURCE_ACTIONS[resource];
  if (!spec) return false;
  const keys = spec[action];
  if (!Array.isArray(keys) || keys.length === 0) return false;
  return hasCapability(accessUser, ...keys);
}

/**
 * Field-level read check. Unknown fields stay visible.
 */
export function canReadField(user, fieldId) {
  const accessUser = toAccessUser(user);
  if (!fieldId) return true;
  if (isAdmin(accessUser.role)) return true;
  const keys = FIELD_PERMISSIONS[fieldId];
  if (!keys) return true;
  if (!isTeamLeader(accessUser.role)) return false;
  return hasCapability(accessUser, ...keys);
}

export function maskValue(user, fieldId, value, placeholder = MASKED_PLACEHOLDER) {
  if (!canReadField(user, fieldId)) return placeholder;
  if (value == null || value === '') return value;
  return value;
}
